/**
 * Chart palette and data shapes shared by the chart components
 * (`components/charts/`) and the pages that feed them. The Vega specs and
 * their labels live in `components/charts/vega-specs.ts`.
 */

// Tableau20 color scheme (matches Vega's tableau20 scheme exactly)
// Source: https://github.com/vega/vega-scale
export const TABLEAU20_COLORS = [
  "#f58518",
  "#ffbf79",
  "#4c78a8",
  "#9ecae9",
  "#54a24b",
  "#88d27a",
  "#b79a20",
  "#f2cf5b",
  "#439894",
  "#83bcb6",
  "#e45756",
  "#ff9d98",
  "#79706e",
  "#bab0ac",
  "#d67195",
  "#fcbfd2",
  "#b279a2",
  "#d6a5c9",
  "#9e765f",
  "#d8b5a5",
];

export type ChartData = {
  id: string;
  label: string;
  value: number;
  color?: string;
};

export type TimeSeriesData = {
  date: string; // YYYY-MM-DD format
  value: number;
  bank?: number; // Bank-savings comparison (same cash flows at a flat bank rate)
};

/** One point of a value-over-time chart, with its tooltip text preformatted
 * by the caller (tenant date/number formats and currency). */
export type ValueHistoryDatum = {
  date: string; // YYYY-MM-DD
  value: number;
  dateLabel: string;
  valueLabel: string;
};

export type MultiLineData = {
  x: string; // Label for x-axis (e.g., "Year 1", "2025")
  y1: number; // Value for first line (without cost)
  y2: number; // Value for second line (with cost)
};

export type ForecastChartData = {
  date: string; // Date in YYYY-MM-DD format
  forecast: number; // Expected value
  lower: number; // Lower confidence bound
  upper: number; // Upper confidence bound
  bank?: number; // Bank-savings comparison (same deposits at a flat bank rate)
};

/** One band's value at one bar, for {@link createNormalizedStackedBarChartSpec}. */
export type StackedBarDatum = {
  /** X-axis category — a period label like "Q2 2026" or "2031". */
  x: string;
  /** Legend key. Rendering order and colour both key off this. */
  series: string;
  value: number;
};

/**
 * QL-313 — a horizontal rule across a normalised stacked bar, for a ceiling
 * that one band must stay under (the equity share chart's max equity share).
 * `value` is a percent of the whole bar (0–100).
 */
export type StackedBarReferenceLine = {
  value: number;
  /** Hover text for the rule (e.g. "Max allowed 25.0%"). Never printed on the chart. */
  label?: string;
  color?: string;
};
